/**
 * SoleadLab 2.0 - Alarm Evaluation Engine
 *
 * Evaluates both simple alarm rules and compound rule groups (AND/OR)
 * against live data point values. Returns the overall alarm state
 * and lists of triggered rules/groups.
 *
 * Same logic runs server-side (Phase 7) for when the browser is closed.
 */

import type {
  AlarmRule,
  AlarmRuleGroup,
  AlarmCondition,
  AlarmOperator,
  AlarmState,
  AlarmSeverity,
  TriggeredRule,
  TriggeredRuleGroup,
  DataPointRuntimeState,
  DataPointConfig,
  ParsedSchema,
  AdvancedAlarmRule,
  ZeroCheckConfig,
  DeviationConfig,
  ExpressionConfig,
  RuleExclusion,
  TriggeredAdvancedRule,
} from '@/types/monitoring';

// ============================================
// Single Condition Evaluator
// ============================================

/**
 * Evaluate a single condition (metric op value) against the actual data.
 */
export function evaluateCondition(
  actual: number | string | null | undefined,
  op: AlarmOperator,
  threshold: number
): boolean {
  if (actual === null || actual === undefined) return false;

  const numValue = typeof actual === 'number' ? actual : parseFloat(String(actual));
  if (isNaN(numValue)) return false;

  switch (op) {
    case '>':
      return numValue > threshold;
    case '>=':
      return numValue >= threshold;
    case '<':
      return numValue < threshold;
    case '<=':
      return numValue <= threshold;
    case '==':
      return numValue === threshold;
    case '!=':
      return numValue !== threshold;
    default:
      return false;
  }
}

// ============================================
// Simple Rule Evaluator
// ============================================

/**
 * Evaluate a single alarm rule against the current values.
 * Returns the TriggeredRule if triggered, null otherwise.
 */
export function evaluateRule(
  rule: AlarmRule,
  values: Record<string, number | string | null>
): TriggeredRule | null {
  if (!rule.enabled) return null;

  const actual = values[rule.metric];
  const triggered = evaluateCondition(actual, rule.op, rule.value);

  if (!triggered) return null;

  return {
    rule_id: rule.id,
    metric: rule.metric,
    op: rule.op,
    value: rule.value,
    actual: actual ?? null,
    severity: rule.severity,
    message: rule.message,
  };
}

// ============================================
// Compound Rule Group Evaluator
// ============================================

/**
 * Evaluate a compound rule group (AND/OR combinator).
 *
 * - AND: ALL conditions must be true to trigger
 * - OR: ANY condition triggers
 */
export function evaluateRuleGroup(
  group: AlarmRuleGroup,
  values: Record<string, number | string | null>
): TriggeredRuleGroup | null {
  if (!group.enabled || group.conditions.length === 0) return null;

  const conditionResults = group.conditions.map((condition: AlarmCondition) => {
    const actual = values[condition.metric];
    const triggered = evaluateCondition(actual, condition.op, condition.value);
    return { condition, actual: actual ?? null, triggered };
  });

  let groupTriggered: boolean;

  if (group.combinator === 'AND') {
    groupTriggered = conditionResults.every((r) => r.triggered);
  } else {
    // OR
    groupTriggered = conditionResults.some((r) => r.triggered);
  }

  if (!groupTriggered) return null;

  return {
    group_id: group.id,
    group_name: group.name,
    combinator: group.combinator,
    severity: group.severity,
    message: group.message,
    triggered_conditions: conditionResults
      .filter((r) => r.triggered)
      .map((r) => ({
        metric: r.condition.metric,
        op: r.condition.op,
        value: r.condition.value,
        actual: r.actual,
      })),
  };
}

// ============================================
// Full Data Point Evaluation
// ============================================

/**
 * Evaluate all alarm rules and rule groups for a single data point.
 * Returns the runtime state including the worst alarm state and all triggered rules.
 *
 * @param config - The data point configuration with rules and rule_groups
 * @param values - Current metric values keyed by metric name
 * @param lastTimestamp - ISO timestamp of the last data update (or null)
 * @param staleAfterSeconds - Seconds after which data is considered stale (default 300)
 */
export function evaluateDataPoint(
  config: DataPointConfig,
  values: Record<string, number | string | null>,
  lastTimestamp: string | null,
  staleAfterSeconds = 300
): DataPointRuntimeState {
  const triggeredRules: TriggeredRule[] = [];
  const triggeredGroups: TriggeredRuleGroup[] = [];

  // Check for no_data
  if (Object.keys(values).length === 0 || lastTimestamp === null) {
    return {
      datapoint_id: config.id,
      values,
      ts: lastTimestamp,
      state: 'no_data',
      triggered_rules: [],
      triggered_groups: [],
    };
  }

  // Check for stale data
  if (lastTimestamp) {
    const lastUpdate = new Date(lastTimestamp).getTime();
    const now = Date.now();
    if (now - lastUpdate > staleAfterSeconds * 1000) {
      return {
        datapoint_id: config.id,
        values,
        ts: lastTimestamp,
        state: 'stale',
        triggered_rules: [],
        triggered_groups: [],
      };
    }
  }

  // Evaluate simple rules
  for (const rule of config.rules ?? []) {
    const result = evaluateRule(rule, values);
    if (result) triggeredRules.push(result);
  }

  // Evaluate compound rule groups
  for (const group of config.rule_groups ?? []) {
    const result = evaluateRuleGroup(group, values);
    if (result) triggeredGroups.push(result);
  }

  // Determine the worst alarm state
  const state = determineWorstState(triggeredRules, triggeredGroups);

  return {
    datapoint_id: config.id,
    values,
    ts: lastTimestamp,
    state,
    triggered_rules: triggeredRules,
    triggered_groups: triggeredGroups,
  };
}

// ============================================
// State Determination
// ============================================

/**
 * Determine the worst alarm state from triggered rules and groups.
 * Priority: critical > warning > ok
 */
function determineWorstState(
  triggeredRules: TriggeredRule[],
  triggeredGroups: TriggeredRuleGroup[]
): AlarmState {
  let worst: AlarmSeverity | null = null;

  for (const rule of triggeredRules) {
    if (rule.severity === 'critical') return 'critical';
    if (rule.severity === 'warning') worst = 'warning';
  }

  for (const group of triggeredGroups) {
    if (group.severity === 'critical') return 'critical';
    if (group.severity === 'warning') worst = 'warning';
  }

  return worst ?? 'ok';
}

// ============================================
// Batch Evaluation
// ============================================

/**
 * Evaluate alarms for ALL data points in a project config.
 * Returns a Map of datapoint_id → DataPointRuntimeState.
 */
export function evaluateAllDataPoints(
  datapoints: DataPointConfig[],
  allValues: Record<string, Record<string, number | string | null>>,
  lastTimestamp: string | null,
  staleAfterSeconds = 300
): Map<string, DataPointRuntimeState> {
  const results = new Map<string, DataPointRuntimeState>();

  // Normalize data: if values are in nested time-series format
  // (category → timestamp → entity → metrics), flatten to entity → metrics
  const flatValues = normalizeEntityValues(allValues);

  for (const dp of datapoints) {
    if (!dp.enabled) continue;

    // Look up values by entity_id
    const values = flatValues[dp.entity_id] ?? {};

    const state = evaluateDataPoint(dp, values, lastTimestamp, staleAfterSeconds);
    results.set(dp.id, state);
  }

  return results;
}

/**
 * Normalize entity values from various formats into flat entity_id → metrics.
 *
 * Handles:
 * 1. Flat format: { "entity_id": { metric: value } } — pass-through
 * 2. Time-series: { "category": { "timestamp": { "entity_id": { metric: value } } } }
 *    → uses latest timestamp for each entity
 * 3. Mixed: some keys flat, some nested
 */
function normalizeEntityValues(
  data: Record<string, Record<string, number | string | null>>
): Record<string, Record<string, number | string | null>> {
  const result: Record<string, Record<string, number | string | null>> = {};

  for (const [key, value] of Object.entries(data)) {
    if (!value || typeof value !== 'object') continue;

    // Check if this is a flat entity (values are primitives: number/string/null)
    const innerValues = Object.values(value);
    const isFlat = innerValues.length > 0 && innerValues.every(
      (v) => v === null || typeof v === 'number' || typeof v === 'string'
    );

    if (isFlat) {
      // Already flat: entity_id → { metric: value }
      result[key] = value;
    } else {
      // Nested: likely category → { timestamp → entities } or timestamp → { entity → metrics }
      // Find the latest timestamp key and extract entities from it
      const timestamps = Object.keys(value).sort();
      const latestKey = timestamps[timestamps.length - 1];
      const latestData = (value as unknown as Record<string, unknown>)[latestKey];

      if (latestData && typeof latestData === 'object' && !Array.isArray(latestData)) {
        const latestRec = latestData as Record<string, unknown>;

        // Check if latestData contains entity objects or direct metrics
        for (const [entityKey, entityVal] of Object.entries(latestRec)) {
          if (entityVal && typeof entityVal === 'object' && !Array.isArray(entityVal)) {
            // This is an entity: entity_id → { metric: value }
            const metrics: Record<string, number | string | null> = {};
            for (const [mk, mv] of Object.entries(entityVal as Record<string, unknown>)) {
              if (mv === null || typeof mv === 'number' || typeof mv === 'string') {
                metrics[mk] = mv as number | string | null;
              }
            }
            if (Object.keys(metrics).length > 0) {
              result[entityKey] = metrics;
            }
          } else if (entityVal === null || typeof entityVal === 'number' || typeof entityVal === 'string') {
            // Direct metric at timestamp level (e.g., basics → timestamp → { metric: value })
            if (!result[key]) result[key] = {};
            result[key][entityKey] = entityVal as number | string | null;
          }
        }
      }
    }
  }

  return result;
}

// ============================================
// Advanced Rule Evaluators (site-level)
// ============================================

/** Check if an entity+metric pair is in the exclusion list. */
function isExcluded(exclusions: RuleExclusion[], entityId: string, metric: string): boolean {
  return exclusions.some((e) => e.entity_id === entityId && e.metric === metric);
}

/**
 * Evaluate a Zero/Threshold Check rule.
 * Finds all metrics matching the prefix across all entities,
 * optionally filtered by entity_type, and checks the condition.
 */
function evaluateZeroCheck(
  config: ZeroCheckConfig,
  allValues: Record<string, Record<string, number | string | null>>,
  schema: ParsedSchema | null,
  exclusions: RuleExclusion[] = []
): { triggered: boolean; matched: Array<{ entity_id: string; metric: string; value: number | string | null }> } {
  const matched: Array<{ entity_id: string; metric: string; value: number | string | null }> = [];
  const checked: Array<{ entity_id: string; metric: string; value: number | string | null; hit: boolean }> = [];

  // Determine which entities to scan
  const entities = schema?.entities ?? [];
  const entityIds = config.entity_type
    ? entities.filter((e) => e.entity_type === config.entity_type).map((e) => e.entity_id)
    : Object.keys(allValues);

  const pattern = config.metric_pattern.toLowerCase();

  for (const entityId of entityIds) {
    const values = allValues[entityId];
    if (!values) continue;

    for (const [metricKey, metricVal] of Object.entries(values)) {
      // Prefix match (case-insensitive)
      if (!metricKey.toLowerCase().startsWith(pattern)) continue;
      // Skip excluded entity+metric pairs
      if (isExcluded(exclusions, entityId, metricKey)) continue;

      const hit = evaluateCondition(metricVal, config.op, config.value);
      checked.push({ entity_id: entityId, metric: metricKey, value: metricVal, hit });
      if (hit) {
        matched.push({ entity_id: entityId, metric: metricKey, value: metricVal });
      }
    }
  }

  if (checked.length === 0) return { triggered: false, matched: [] };

  const triggered = config.match_mode === 'any'
    ? matched.length > 0
    : checked.every((c) => c.hit);

  return { triggered, matched: triggered ? matched : [] };
}

/**
 * Evaluate a Deviation Detection rule.
 * Compares the same metric across all entities of a type.
 * Alerts if any entity deviates by more than threshold_pct from average or max.
 */
function evaluateDeviation(
  config: DeviationConfig,
  allValues: Record<string, Record<string, number | string | null>>,
  schema: ParsedSchema | null,
  exclusions: RuleExclusion[] = []
): { triggered: boolean; matched: Array<{ entity_id: string; metric: string; value: number | string | null }> } {
  const entities = schema?.entities ?? [];
  const targetEntities = entities.filter((e) => e.entity_type === config.entity_type);

  // Collect numeric values for the metric across all matching entities
  const readings: Array<{ entity_id: string; value: number }> = [];
  for (const entity of targetEntities) {
    const values = allValues[entity.entity_id];
    if (!values) continue;
    // Skip excluded entity+metric pairs
    if (isExcluded(exclusions, entity.entity_id, config.metric)) continue;
    const raw = values[config.metric];
    if (raw === null || raw === undefined) continue;
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw));
    if (isNaN(num)) continue;
    // Skip values below min_value threshold (avoid night-time false positives)
    if (config.min_value !== undefined && num < config.min_value) continue;
    readings.push({ entity_id: entity.entity_id, value: num });
  }

  if (readings.length < 2) return { triggered: false, matched: [] };

  // Calculate reference value
  let reference: number;
  if (config.reference === 'max') {
    reference = Math.max(...readings.map((r) => r.value));
  } else {
    reference = readings.reduce((sum, r) => sum + r.value, 0) / readings.length;
  }

  if (reference === 0) return { triggered: false, matched: [] };

  // Check each entity's deviation
  const matched: Array<{ entity_id: string; metric: string; value: number | string | null }> = [];
  for (const reading of readings) {
    const deviationPct = Math.abs(reading.value - reference) / Math.abs(reference) * 100;
    if (deviationPct > config.threshold_pct) {
      matched.push({ entity_id: reading.entity_id, metric: config.metric, value: reading.value });
    }
  }

  return { triggered: matched.length > 0, matched };
}

/**
 * Resolve a token to a numeric value.
 * Handles: variable references (v1, v2...), plain numbers, simple arithmetic (v1 * 0.9).
 */
function resolveToken(
  token: string,
  varValues: Record<string, number | null>
): number | null {
  token = token.trim();

  // Plain number
  const num = parseFloat(token);
  if (!isNaN(num) && !token.match(/^v\d+/)) return num;

  // Variable reference
  if (/^v\d+$/.test(token)) {
    return varValues[token] ?? null;
  }

  // Simple arithmetic: "v1 * 0.9", "v2 + 100", "v3 - 50", "v4 / 2"
  const arithMatch = token.match(/^(v\d+)\s*([+\-*/])\s*(.+)$/);
  if (arithMatch) {
    const leftVal = varValues[arithMatch[1]] ?? null;
    if (leftVal === null) return null;
    const rightVal = parseFloat(arithMatch[3]);
    if (isNaN(rightVal)) return null;
    switch (arithMatch[2]) {
      case '+': return leftVal + rightVal;
      case '-': return leftVal - rightVal;
      case '*': return leftVal * rightVal;
      case '/': return rightVal !== 0 ? leftVal / rightVal : null;
    }
  }

  return null;
}

/**
 * Evaluate a Custom Expression rule.
 * Resolves variables to actual values, evaluates conditions with AND/OR combinator.
 */
function evaluateExpression(
  config: ExpressionConfig,
  allValues: Record<string, Record<string, number | string | null>>,
  exclusions: RuleExclusion[] = []
): { triggered: boolean; details: string } {
  // Resolve all variables to numeric values
  const varValues: Record<string, number | null> = {};
  const varDetails: string[] = [];

  for (const v of config.variables) {
    // Treat excluded variable as null
    if (isExcluded(exclusions, v.entity_id, v.metric)) {
      varValues[v.name] = null;
      varDetails.push(`${v.name}=excluded`);
      continue;
    }
    const entityValues = allValues[v.entity_id];
    const raw = entityValues?.[v.metric];
    if (raw === null || raw === undefined) {
      varValues[v.name] = null;
    } else {
      const num = typeof raw === 'number' ? raw : parseFloat(String(raw));
      varValues[v.name] = isNaN(num) ? null : num;
    }
    varDetails.push(`${v.name}=${varValues[v.name] ?? 'null'}`);
  }

  // Evaluate each condition
  const conditionResults: boolean[] = [];
  const condDetails: string[] = [];

  for (const cond of config.conditions) {
    const leftVal = resolveToken(cond.left, varValues);
    const rightVal = resolveToken(cond.right, varValues);

    if (leftVal === null || rightVal === null) {
      conditionResults.push(false);
      condDetails.push(`${cond.left} ${cond.op} ${cond.right} → null`);
      continue;
    }

    const hit = evaluateCondition(leftVal, cond.op, rightVal);
    conditionResults.push(hit);
    condDetails.push(`${cond.left}(${leftVal}) ${cond.op} ${cond.right}(${rightVal}) → ${hit}`);
  }

  const triggered = config.combinator === 'AND'
    ? conditionResults.every(Boolean)
    : conditionResults.some(Boolean);

  const details = `${varDetails.join(', ')} | ${condDetails.join(` ${config.combinator} `)}`;

  return { triggered, details };
}

/**
 * Evaluate a single advanced alarm rule against all entity values.
 * Returns a TriggeredAdvancedRule if triggered, null otherwise.
 */
export function evaluateAdvancedRule(
  rule: AdvancedAlarmRule,
  allValues: Record<string, Record<string, number | string | null>>,
  schema: ParsedSchema | null
): TriggeredAdvancedRule | null {
  if (!rule.enabled) return null;

  switch (rule.type) {
    case 'zero_check': {
      if (!rule.zero_check) return null;
      const result = evaluateZeroCheck(rule.zero_check, allValues, schema, rule.exclusions);
      if (!result.triggered) return null;
      const matchSummary = result.matched.slice(0, 5)
        .map((m) => `${m.entity_id}.${m.metric}=${m.value}`)
        .join(', ');
      return {
        rule_id: rule.id,
        rule_name: rule.name,
        rule_type: 'zero_check',
        severity: rule.severity,
        message: rule.message,
        details: `${rule.zero_check.metric_pattern}* ${rule.zero_check.op} ${rule.zero_check.value} (${rule.zero_check.match_mode}): ${matchSummary}`,
        matched_metrics: result.matched,
      };
    }

    case 'deviation': {
      if (!rule.deviation) return null;
      const result = evaluateDeviation(rule.deviation, allValues, schema, rule.exclusions);
      if (!result.triggered) return null;
      const matchSummary = result.matched.slice(0, 5)
        .map((m) => `${m.entity_id}=${m.value}`)
        .join(', ');
      return {
        rule_id: rule.id,
        rule_name: rule.name,
        rule_type: 'deviation',
        severity: rule.severity,
        message: rule.message,
        details: `${rule.deviation.metric} deviation >${rule.deviation.threshold_pct}% from ${rule.deviation.reference}: ${matchSummary}`,
        matched_metrics: result.matched,
      };
    }

    case 'expression': {
      if (!rule.expression) return null;
      const result = evaluateExpression(rule.expression, allValues, rule.exclusions);
      if (!result.triggered) return null;
      return {
        rule_id: rule.id,
        rule_name: rule.name,
        rule_type: 'expression',
        severity: rule.severity,
        message: rule.message,
        details: result.details,
      };
    }

    default:
      return null;
  }
}

/**
 * Evaluate all advanced rules against the current entity values.
 * Returns an array of triggered advanced rules.
 */
export function evaluateAdvancedRules(
  rules: AdvancedAlarmRule[],
  allValues: Record<string, Record<string, number | string | null>>,
  schema: ParsedSchema | null
): TriggeredAdvancedRule[] {
  const triggered: TriggeredAdvancedRule[] = [];
  for (const rule of rules) {
    const result = evaluateAdvancedRule(rule, allValues, schema);
    if (result) triggered.push(result);
  }
  return triggered;
}

// ============================================
// Human-readable Descriptions
// ============================================

/**
 * Generate a human-readable description of a rule group.
 * e.g., "P_AC < 100 AND T_WR > 80"
 */
export function describeRuleGroup(group: AlarmRuleGroup): string {
  if (group.conditions.length === 0) return '(no conditions)';

  return group.conditions
    .map((c) => `${c.metric} ${c.op} ${c.value}`)
    .join(` ${group.combinator} `);
}

/**
 * Generate a human-readable description of a simple rule.
 * e.g., "P_AC > 500 (critical)"
 */
export function describeRule(rule: AlarmRule): string {
  return `${rule.metric} ${rule.op} ${rule.value} (${rule.severity})`;
}

// ============================================
// Entity Value Extraction from Raw JSON
// ============================================

/**
 * Walk an object recursively looking for a key matching entityId.
 * Returns the value at that key (expected to be a record of metric→value).
 */
function findEntityData(
  obj: unknown,
  entityId: string,
): Record<string, unknown> | null {
  if (!obj || typeof obj !== 'object') return null;
  const rec = obj as Record<string, unknown>;

  // Direct key match
  if (entityId in rec && rec[entityId] && typeof rec[entityId] === 'object') {
    return rec[entityId] as Record<string, unknown>;
  }

  // Recurse into nested objects and arrays
  for (const val of Object.values(rec)) {
    if (Array.isArray(val)) {
      for (const item of val) {
        const found = findEntityData(item, entityId);
        if (found) return found;
      }
    } else if (val && typeof val === 'object') {
      const found = findEntityData(val, entityId);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Extract entity metric values from a raw uploaded JSON payload
 * using the loaded schema. Maps entity_id → { metric_key → value }.
 */
export function extractEntityValues(
  jsonPayload: unknown,
  schema: ParsedSchema | null,
): Record<string, Record<string, number | string | null>> {
  if (!schema || !jsonPayload || typeof jsonPayload !== 'object') return {};

  const result: Record<string, Record<string, number | string | null>> = {};

  // First, try to use the JSON as a flat entity→metrics map
  const json = jsonPayload as Record<string, unknown>;

  for (const entity of schema.entities) {
    const entityData = findEntityData(json, entity.entity_id);
    if (entityData) {
      result[entity.entity_id] = {};
      for (const metric of entity.metrics) {
        const val = entityData[metric.key];
        if (val !== undefined && val !== null) {
          result[entity.entity_id][metric.key] =
            typeof val === 'number' ? val :
            typeof val === 'string' ? (isNaN(Number(val)) ? val : Number(val)) :
            null;
        }
      }
    }
  }

  return result;
}
